import { useMemo, useState, type Key } from "react";
import { Button, Select, Space, Tooltip, Typography, message } from "antd";
import type { ColumnType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { useGetProctoringAssignments, useSaveProctoringAssignments, type ProctoringAssignmentItem } from "@hooks";
import type { ExamPeriodMonitor } from "@types";

/// Assessor assignment on the term monitoring table: a per-candidate column plus bulk actions for selected/all rows.
export const useMonitorAssessorAssignments = (examTermId: string | undefined, enabled: boolean, rows: ExamPeriodMonitor[] | undefined) => {
  const { t } = useTranslation();
  const { data } = useGetProctoringAssignments(examTermId ?? "", enabled && !!examTermId);
  const save = useSaveProctoringAssignments(examTermId ?? "");
  const [selected, setSelected] = useState<Key[]>([]);
  const [bulkAssessor, setBulkAssessor] = useState<string | undefined>();

  const assessorOf = useMemo(() => {
    const map: Record<string, string | null | undefined> = {};
    data?.candidates.forEach((c) => (map[c.examInstanceId] = c.assessorId));
    return map;
  }, [data]);
  const options = useMemo(
    () => (data?.assessors ?? []).map((a) => ({ value: a.assessorId, label: a.name || a.email })),
    [data]
  );

  const apply = (ids: string[], assessorId: string | null) => {
    if (ids.length === 0) return;
    const items: ProctoringAssignmentItem[] = ids.map((examInstanceId) => ({ examInstanceId, assessorId }));
    save.mutate(items, {
      onSuccess: () => {
        message.success(t("proctoring.saved"));
        setSelected([]);
      },
      onError: () => message.error(t("proctoring.saveFailed")),
    });
  };

  const column: ColumnType<ExamPeriodMonitor> = {
    title: t("proctoring.assessor"),
    key: "proctorAssessor",
    width: 200,
    render: (_, record) => (
      <Select
        size="small"
        allowClear
        style={{ width: "100%" }}
        placeholder={t("proctoring.unassigned")}
        value={assessorOf[record.id] ?? undefined}
        options={options}
        loading={save.isPending}
        onChange={(value) => apply([record.id], value ?? null)}
        onClick={(e) => e.stopPropagation()}
        data-testid="monitor-assessor-select"
      />
    ),
  };

  const allIds = (rows ?? []).map((r) => r.id);
  const selectedIds = selected.map(String);
  const unassignedCount = allIds.filter((id) => !assessorOf[id]).length;

  const toolbar = (
    <Space wrap style={{ marginTop: 8 }} data-testid="monitor-assign-toolbar">
      <Typography.Text strong>{t("proctoring.assignments")}:</Typography.Text>
      <Select
        size="small"
        style={{ width: 220 }}
        placeholder={t("proctoring.chooseAssessor")}
        value={bulkAssessor}
        onChange={setBulkAssessor}
        options={options}
        allowClear
      />
      <Button size="small" disabled={!bulkAssessor || selectedIds.length === 0} loading={save.isPending} onClick={() => apply(selectedIds, bulkAssessor!)}>
        {t("proctoring.assignSelected", { count: selectedIds.length })}
      </Button>
      <Tooltip title={t("proctoring.assignAllHint")}>
        <Button size="small" disabled={!bulkAssessor || allIds.length === 0} loading={save.isPending} onClick={() => apply(allIds, bulkAssessor!)}>
          {t("proctoring.assignAll", { count: allIds.length })}
        </Button>
      </Tooltip>
      <Button size="small" danger disabled={selectedIds.length === 0} loading={save.isPending} onClick={() => apply(selectedIds, null)}>
        {t("proctoring.unassignSelected", { count: selectedIds.length })}
      </Button>
      <Typography.Text type="secondary">{t("proctoring.unassignedCount", { count: unassignedCount })}</Typography.Text>
    </Space>
  );

  const rowSelection = { selectedRowKeys: selected, onChange: (keys: Key[]) => setSelected(keys) };

  return { column, toolbar, rowSelection };
};
